/**
 * Authentication Service
 * Handles Supabase Email OTP sign-in, passphrase management, and vault encryption
 */

import { supabase } from './supabase-client';
import {
  deriveVaultKey,
  generateDeviceKey,
  wrapKey,
  unwrapKey,
  encryptData,
  decryptData,
  encryptDeviceKey,
  decryptDeviceKey,
} from '../utils/crypto-utils';
import {
  storeDeviceKey,
  getDeviceKey,
  storeVault,
  getVault,
  removeDeviceKey,
  removeVault,
  generateDeviceId,
  hasDeviceKey,
} from '../utils/indexeddb-storage';

const VAULT_SALT_STORAGE_KEY = 'vault_salt';

/**
 * Send Magic Link to email for sign-in/sign-up
 * Note: signInWithOtp sends Magic Links by default (not OTP codes)
 * To use OTP codes, modify email template in Supabase Dashboard to include {{ .Token }}
 * @param {string} email - User email
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function sendMagicLink(email) {
  try {
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return { 
        success: false, 
        error: 'Invalid email address format',
        errorCode: 'INVALID_EMAIL'
      };
    }

    const { data, error } = await supabase.auth.signInWithOtp({
      email: email.trim().toLowerCase(),
      options: {
        shouldCreateUser: true,
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });

    if (error) {
      // Map common Supabase errors to user-friendly messages
      let errorMessage = error.message;
      let errorCode = error.status?.toString() || 'UNKNOWN_ERROR';

      // Handle specific error cases
      if (error.message?.includes('rate limit') || error.message?.includes('too many requests')) {
        errorMessage = 'Too many requests. Please wait a few minutes before trying again. (Rate limit: 2 emails/hour with default Supabase email service)';
        errorCode = 'RATE_LIMIT';
      } else if (error.message?.includes('Email not confirmed')) {
        errorMessage = 'Please check your email and confirm your account first.';
        errorCode = 'EMAIL_NOT_CONFIRMED';
      } else if (error.message?.includes('invalid') || error.message?.includes('Invalid')) {
        errorMessage = 'Invalid email address or authentication configuration issue.';
        errorCode = 'INVALID_REQUEST';
      } else if (error.status === 429) {
        errorMessage = 'Rate limit exceeded. Please wait 1 hour before requesting another OTP.';
        errorCode = 'RATE_LIMIT_EXCEEDED';
      }

      return { 
        success: false, 
        error: errorMessage,
        errorCode,
        errorDetails: error
      };
    }

    // Success - Supabase accepted the request
    // Magic Link is sent by default (not OTP code)
    // The email contains a link that redirects back to emailRedirectTo URL
    // Supabase JS client with detectSessionInUrl: true will automatically handle the callback
    return { 
      success: true,
      message: 'Magic link sent! Check your email and click the link to sign in.',
      data
    };
  } catch (error) {
    // Network or unexpected errors
    return { 
      success: false, 
      error: error.message || 'Network error. Please check your connection and try again.',
      errorCode: 'NETWORK_ERROR',
      errorDetails: error
    };
  }
}

/**
 * Verify OTP and sign in user
 * @param {string} email - User email
 * @param {string} token - OTP token from email
 * @returns {Promise<{success: boolean, user?: object, session?: object, error?: string, needsPassphrase?: boolean}>}
 */
export async function verifyOTP(email, token) {
  try {
    // Validate token format (typically 6 digits)
    if (!token || token.length < 6) {
      return { 
        success: false, 
        error: 'Invalid OTP code. Please enter the 6-digit code from your email.',
        errorCode: 'INVALID_OTP_FORMAT'
      };
    }

    const { data, error } = await supabase.auth.verifyOtp({
      email: email.trim().toLowerCase(),
      token: token.trim(),
      type: 'email',
    });

    if (error) {
      // Map common OTP verification errors
      let errorMessage = error.message;
      let errorCode = 'OTP_VERIFICATION_FAILED';

      if (error.message?.includes('expired') || error.message?.includes('Expired')) {
        errorMessage = 'OTP code has expired. Please request a new code.';
        errorCode = 'OTP_EXPIRED';
      } else if (error.message?.includes('invalid') || error.message?.includes('Invalid')) {
        errorMessage = 'Invalid OTP code. Please check the code and try again.';
        errorCode = 'OTP_INVALID';
      } else if (error.message?.includes('token')) {
        errorMessage = 'Invalid or expired OTP code. Please request a new code.';
        errorCode = 'OTP_TOKEN_INVALID';
      }

      return { 
        success: false, 
        error: errorMessage,
        errorCode,
        errorDetails: error
      };
    }

    if (!data.user || !data.session) {
      return { 
        success: false, 
        error: 'Verification succeeded but session was not created. Please try again.',
        errorCode: 'SESSION_CREATION_FAILED'
      };
    }

    const userId = data.user.id;
    const deviceId = generateDeviceId();

    // Check if device key exists (user has set passphrase before on this device)
    const deviceKeyExists = await hasDeviceKey(userId, deviceId);

    return {
      success: true,
      user: data.user,
      session: data.session,
      needsPassphrase: !deviceKeyExists, // First time on this device
      deviceId,
      message: 'OTP verified successfully!',
    };
  } catch (error) {
    return { 
      success: false, 
      error: error.message || 'Network error. Please check your connection and try again.',
      errorCode: 'NETWORK_ERROR',
      errorDetails: error
    };
  }
}

/**
 * Set passphrase for first time on a device
 * This derives the vault key, generates device key, and stores wrapped keys
 * @param {string} passphrase - User passphrase
 * @param {string} userId - Supabase user ID
 * @param {string} deviceId - Device ID
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function setPassphrase(passphrase, userId, deviceId) {
  try {
    // Step 1: Derive vault key from passphrase
    const { key: vaultKey, salt } = await deriveVaultKey(passphrase);
    
    // Store salt in localStorage and Supabase user metadata for cross-device access
    localStorage.setItem(`${VAULT_SALT_STORAGE_KEY}_${userId}`, salt);
    
    // Also store in Supabase user metadata for access on new devices
    await supabase.auth.updateUser({
      data: { vault_salt: salt }
    });

    // Step 2: Generate random device key
    const { key: deviceKey, raw: deviceKeyRaw } = await generateDeviceKey();

    // Step 3: Wrap vault key with device key (for auto-unlock on this device)
    const wrappedVaultKey = await wrapKey(vaultKey, deviceKey);

    // Step 4: Store device key and wrapped vault key in IndexedDB
    // Device key is stored as raw base64 for auto-unlock (protected by browser security)
    // The wrapped vault key allows unlocking without passphrase on this device
    const deviceKeyBase64 = arrayBufferToBase64(deviceKeyRaw);
    await storeDeviceKey(userId, deviceKeyBase64, deviceId);
    
    // Store wrapped vault key separately (we'll store it with the device key record)
    // Actually, let's enhance the storage to store both
    await storeWrappedVaultKey(userId, wrappedVaultKey, deviceId);

    // Step 5: Initialize empty vault (can be populated later with wallet keys)
    const emptyVault = JSON.stringify({ wallets: [] });
    const encryptedVault = await encryptData(emptyVault, vaultKey);
    // Store in both IndexedDB and Supabase (for cross-device recovery)
    await storeVault(userId, encryptedVault, true);

    return { success: true };
  } catch (error) {
    console.error('Error setting passphrase:', error);
    return { success: false, error: error.message || 'Failed to set passphrase' };
  }
}

/**
 * Unlock vault using device key (auto-unlock on same device)
 * @param {string} userId - Supabase user ID
 * @param {string} deviceId - Device ID
 * @returns {Promise<{success: boolean, vault?: object, vaultKey?: CryptoKey, error?: string}>}
 */
export async function unlockVaultWithDevice(userId, deviceId) {
  try {
    // Step 1: Get device key from IndexedDB (stored as raw base64 for auto-unlock)
    const deviceKeyBase64 = await getDeviceKey(userId, deviceId);
    if (!deviceKeyBase64) {
      return { success: false, error: 'Device key not found. Please set passphrase first.' };
    }

    // Step 2: Get wrapped vault key
    const wrappedVaultKey = await getWrappedVaultKey(userId, deviceId);
    if (!wrappedVaultKey) {
      return { success: false, error: 'Wrapped vault key not found. Please set passphrase first.' };
    }

    // Step 3: Import device key as CryptoKey
    const deviceKeyBuffer = base64ToArrayBuffer(deviceKeyBase64);
    const deviceKey = await crypto.subtle.importKey(
      'raw',
      deviceKeyBuffer,
      { name: 'AES-GCM', length: 256 },
      false,
      ['unwrapKey']
    );

    // Step 4: Unwrap vault key using device key
    const vaultKey = await unwrapKey(wrappedVaultKey, deviceKey);

    // Step 5: Get and decrypt vault (checks IndexedDB first, then Supabase for cross-device recovery)
    const encryptedVault = await getVault(userId, true); // true = check Supabase if not in IndexedDB
    if (!encryptedVault) {
      // Initialize empty vault
      const emptyVault = JSON.stringify({ wallets: [] });
      const newEncryptedVault = await encryptData(emptyVault, vaultKey);
      // Store in both IndexedDB and Supabase (for cross-device recovery)
      await storeVault(userId, newEncryptedVault, true);
      return { success: true, vault: { wallets: [] }, vaultKey };
    }

    const decryptedVault = await decryptData(encryptedVault, vaultKey);
    const vault = JSON.parse(decryptedVault);

    return { success: true, vault, vaultKey };
  } catch (error) {
    console.error('Error unlocking vault with device:', error);
    return { success: false, error: error.message || 'Failed to unlock vault' };
  }
}

/**
 * Unlock vault using passphrase
 * @param {string} passphrase - User passphrase
 * @param {string} userId - Supabase user ID
 * @param {string} deviceId - Device ID (optional, for auto-unlock setup)
 * @returns {Promise<{success: boolean, vault?: object, error?: string}>}
 */
export async function unlockVaultWithPassphrase(passphrase, userId, deviceId = null) {
  try {
    // Step 1: Get salt (stored locally or from Supabase metadata)
    let salt = localStorage.getItem(`${VAULT_SALT_STORAGE_KEY}_${userId}`);
    
    // If no salt found, try to get from Supabase user metadata
    if (!salt) {
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.user_metadata?.vault_salt) {
        salt = user.user_metadata.vault_salt;
      } else {
        return { success: false, error: 'Vault salt not found. Account may need re-initialization.' };
      }
    }

    // Step 2: Derive vault key from passphrase
    const { key: vaultKey } = await deriveVaultKey(passphrase, salt);

    // Step 3: Get encrypted vault (checks IndexedDB first, then Supabase for cross-device recovery)
    const encryptedVault = await getVault(userId, true); // true = check Supabase if not in IndexedDB
    if (!encryptedVault) {
      // Initialize empty vault if none exists
      const emptyVault = JSON.stringify({ wallets: [] });
      const newEncryptedVault = await encryptData(emptyVault, vaultKey);
      // Store in both IndexedDB and Supabase (for cross-device recovery)
      await storeVault(userId, newEncryptedVault, true);
      return { success: true, vault: { wallets: [] }, vaultKey };
    }

    // Step 4: Decrypt vault
    const decryptedVault = await decryptData(encryptedVault, vaultKey);
    const vault = JSON.parse(decryptedVault);

    // Step 5: If deviceId provided and device key doesn't exist, set it up for auto-unlock
    if (deviceId && !(await hasDeviceKey(userId, deviceId))) {
      await setupDeviceKeyForAutoUnlock(vaultKey, userId, deviceId);
    }

    return { success: true, vault, vaultKey };
  } catch (error) {
    console.error('Error unlocking vault with passphrase:', error);
    return { success: false, error: error.message || 'Failed to unlock vault' };
  }
}

/**
 * Setup device key for auto-unlock (after passphrase unlock)
 * @param {CryptoKey} vaultKey - Vault key
 * @param {string} userId - Supabase user ID
 * @param {string} deviceId - Device ID
 */
async function setupDeviceKeyForAutoUnlock(vaultKey, userId, deviceId) {
  try {
    // Generate device key
    const { key: deviceKey, raw: deviceKeyRaw } = await generateDeviceKey();

    // Wrap vault key with device key (for auto-unlock)
    const wrappedVaultKey = await wrapKey(vaultKey, deviceKey);

    // Store device key as raw base64 (protected by browser security/IndexedDB isolation)
    // This allows auto-unlock without passphrase on this device
    const deviceKeyBase64 = arrayBufferToBase64(deviceKeyRaw);
    await storeDeviceKey(userId, deviceKeyBase64, deviceId);
    await storeWrappedVaultKey(userId, wrappedVaultKey, deviceId);
  } catch (error) {
    console.error('Error setting up device key:', error);
    throw error;
  }
}

/**
 * Save vault data (encrypt and store)
 * @param {object} vault - Vault data object
 * @param {string} userId - Supabase user ID
 * @param {CryptoKey} vaultKey - Vault key (from unlocked session)
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function saveVault(vault, userId, vaultKey) {
  try {
    const vaultJson = JSON.stringify(vault);
    const encryptedVault = await encryptData(vaultJson, vaultKey);
    // Store in both IndexedDB and Supabase (for cross-device recovery)
    await storeVault(userId, encryptedVault, true);
    return { success: true };
  } catch (error) {
    console.error('Error saving vault:', error);
    return { success: false, error: error.message || 'Failed to save vault' };
  }
}

/**
 * Check if vault is initialized for a user (has salt)
 * @param {string} userId - Supabase user ID
 * @returns {Promise<boolean>} - True if vault is initialized
 */
export async function isVaultInitialized(userId) {
  try {
    // Check localStorage first
    let salt = localStorage.getItem(`${VAULT_SALT_STORAGE_KEY}_${userId}`);
    
    // If not in localStorage, check Supabase user metadata
    if (!salt) {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error || !user) {
        return false;
      }
      salt = user?.user_metadata?.vault_salt;
    }
    
    return !!salt;
  } catch (error) {
    console.error('Error checking vault initialization:', error);
    return false;
  }
}

/**
 * Get current session
 * @returns {Promise<{user?: object, session?: object}>}
 */
export async function getSession() {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) throw error;
    return { user: session?.user, session };
  } catch (error) {
    console.error('Error getting session:', error);
    return { user: null, session: null };
  }
}

/**
 * Sign out user
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function signOut() {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error signing out:', error);
    return { success: false, error: error.message || 'Failed to sign out' };
  }
}

/**
 * Listen to auth state changes
 * @param {Function} callback - Callback function (user) => void
 * @returns {Function} - Unsubscribe function
 */
export function onAuthStateChange(callback) {
  return supabase.auth.onAuthStateChange((event, session) => {
    callback(session?.user || null, event, session);
  });
}

/**
 * Store wrapped vault key for auto-unlock
 */
async function storeWrappedVaultKey(userId, wrappedVaultKey, deviceId) {
  // Store in localStorage with device-specific key
  localStorage.setItem(`wrapped_vault_key_${userId}_${deviceId}`, wrappedVaultKey);
}

/**
 * Get wrapped vault key for auto-unlock
 */
async function getWrappedVaultKey(userId, deviceId) {
  return localStorage.getItem(`wrapped_vault_key_${userId}_${deviceId}`);
}

/**
 * Helper: Convert ArrayBuffer to base64
 */
function arrayBufferToBase64(buffer) {
  if (buffer instanceof ArrayBuffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }
  // If it's already a string (from encryptDeviceKey which returns base64), return as-is
  return buffer;
}

/**
 * Helper: Convert base64 to ArrayBuffer
 */
function base64ToArrayBuffer(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}
